import { useState, useRef, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { useAuth, hasPermission } from "../../lib/auth";
import { Card, CardHeader, Button, Badge, Field, Input, Textarea, Select, Spinner, EmptyState, ErrorText } from "../../components/ui";
import { StatusBadge } from "../../components/StatusBadge";
import { nextTransitions } from "../../lib/transitions";
import { PIPELINE_ORDER, PIPELINE_LABELS, type PipelineStage } from "../../lib/pipeline";
import { date, dateTime, km, money, statusLabel } from "../../lib/format";
import type { CaseRow, ClientRow, VehicleRow, DocumentRow, PaymentRow, HistoryRow, Page, CaseStatus } from "../../lib/types";

export function CrmCaseDetail() {
  const { id = "" } = useParams();
  const { principal } = useAuth();
  const base = `/api/crm/cases/${id}`;
  const caseQ = useApi<CaseRow>(base);
  const c = caseQ.data;
  const clientQ = useApi<ClientRow>(c ? `/api/crm/clients/${c.clientId}` : null);
  const vehicleQ = useApi<VehicleRow>(c ? `/api/crm/vehicles/${c.vehicleId}` : null);
  const docsQ = useApi<Page<DocumentRow>>(`${base}/documents`);
  const historyQ = useApi<Page<HistoryRow>>(`${base}/history`);
  const paymentsQ = useApi<Page<PaymentRow>>(`${base}/payments`);

  if (caseQ.loading) return <Spinner />;
  if (!c) return <EmptyState>No se encontró el expediente.</EmptyState>;
  const can = (p: string) => hasPermission(principal, p);

  return (
    <>
      <div className="mb-6">
        <Link to="/cases" className="text-sm text-muted hover:underline">← Expedientes</Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="font-mono text-2xl font-semibold tracking-tight">{c.publicRef}</h1>
          <StatusBadge status={c.status} />
          {can("case:write") && (
            <label className="flex items-center gap-2 text-xs text-muted">
              Pipeline:
              <select
                defaultValue={c.pipelineStage}
                onChange={(e) => void api.patch(`/api/crm/cases/${id}/pipeline`, { stage: e.target.value as PipelineStage }).then(() => caseQ.reload())}
                className="rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink"
              >
                {PIPELINE_ORDER.map((st) => <option key={st} value={st}>{PIPELINE_LABELS[st]}</option>)}
              </select>
            </label>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          {can("case:transition") && <TransitionPanel caseId={id} status={c.status} onDone={() => { void caseQ.reload(); void historyQ.reload(); }} />}
          <DocumentsPanel caseId={id} query={docsQ} canReview={can("document:review")} canUpload={can("document:write")} />
          {can("inspection:read") && <InspectionPanel caseId={id} canWrite={can("inspection:write")} />}
          {can("report:read") && <ReportPanel caseId={id} canPublish={can("report:publish")} />}
          <PaymentsPanel caseId={id} query={paymentsQ} canWrite={can("payment:write")} onPaid={() => void caseQ.reload()} />
          {can("note:internal:read") && <NotesPanel caseId={id} canWrite={can("note:internal:write")} />}
        </div>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader title="Cliente y vehículo" />
            <dl className="divide-y divide-line text-sm">
              <Row k="Cliente" v={clientQ.data?.displayName ?? "…"} />
              <Row k="Vehículo" v={vehicleQ.data ? `${vehicleQ.data.make} ${vehicleQ.data.model}` : "…"} />
              <Row k="Año" v={vehicleQ.data?.year ?? "—"} />
              <Row k="VIN" v={vehicleQ.data?.vin ?? "—"} mono />
              <Row k="Km" v={vehicleQ.data ? km(vehicleQ.data.mileageKm) : "—"} />
              <Row k="País" v={c.country} />
              <Row k="Importe" v={money(c.priceMinor, c.currency ?? "EUR")} />
              {c.notes && <Row k="Notas" v={c.notes} />}
            </dl>
          </Card>
          <TimelinePanel history={historyQ.data?.data ?? []} />
        </div>
      </div>
    </>
  );
}

function Row({ k, v, mono }: { k: string; v: string | number; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between px-5 py-2.5">
      <dt className="text-muted">{k}</dt>
      <dd className={mono ? "font-mono" : "tnum"}>{v}</dd>
    </div>
  );
}

function TransitionPanel({ caseId, status, onDone }: { caseId: string; status: CaseStatus; onDone: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const targets = nextTransitions(status);
  if (targets.length === 0) return null;

  async function go(to: CaseStatus) {
    setBusy(to);
    setError(null);
    try {
      await api.post(`/api/crm/cases/${caseId}/transition`, { toStatus: to });
      onDone();
    } catch {
      setError(`No se pudo cambiar a “${statusLabel(to)}”.`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader title="Cambiar estado" />
      <div className="flex flex-wrap gap-2 p-5">
        {targets.map((t) => (
          <Button key={t} variant={t === "CANCELLED" ? "danger" : "ghost"} disabled={busy !== null} onClick={() => void go(t)}>
            {busy === t ? "…" : statusLabel(t)}
          </Button>
        ))}
      </div>
      {error && <div className="px-5 pb-4"><ErrorText>{error}</ErrorText></div>}
    </Card>
  );
}

function DocumentsPanel({ caseId, query, canReview, canUpload }: { caseId: string; query: ReturnType<typeof useApi<Page<DocumentRow>>>; canReview: boolean; canUpload: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const docs = query.data?.data ?? [];

  async function review(docId: string, decision: "APPROVED" | "REJECTED", why?: string) {
    await api.post(`/api/crm/documents/${docId}/review`, { decision, reason: why });
    setRejecting(null);
    setReason("");
    await query.reload();
  }
  async function upload(file: File) {
    await api.upload(`/api/crm/cases/${caseId}/documents`, file);
    await query.reload();
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <Card>
      <CardHeader
        title="Documentos"
        action={canUpload ? (
          <>
            <input ref={inputRef} type="file" hidden accept="application/pdf,image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
            <Button variant="ghost" onClick={() => inputRef.current?.click()}>Subir</Button>
          </>
        ) : undefined}
      />
      {docs.length === 0 ? (
        <div className="p-6 text-sm text-muted">Sin documentos.</div>
      ) : (
        <ul className="divide-y divide-line">
          {docs.map((d) => (
            <li key={d.id} className="px-5 py-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{d.originalFilename}</div>
                  <div className="text-xs text-muted">{date(d.createdAt)} · {(d.sizeBytes / 1024).toFixed(0)} KB · escaneo {d.scanStatus}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge tone={d.status === "APPROVED" ? "ok" : d.status === "REJECTED" ? "danger" : "warn"}>{d.status}</Badge>
                  <button onClick={() => void api.download(`/api/crm/documents/${d.id}/content`, d.originalFilename)} className="text-sm font-medium text-accent-600 hover:underline">Ver</button>
                </div>
              </div>
              {canReview && d.status === "PENDING_REVIEW" && (
                <div className="mt-2 flex items-center gap-2">
                  <Button variant="ghost" onClick={() => void review(d.id, "APPROVED")}>Aprobar</Button>
                  {rejecting === d.id ? (
                    <>
                      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo del rechazo" className="max-w-xs" />
                      <Button variant="danger" disabled={!reason} onClick={() => void review(d.id, "REJECTED", reason)}>Confirmar</Button>
                    </>
                  ) : (
                    <Button variant="ghost" onClick={() => setRejecting(d.id)}>Rechazar</Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function InspectionPanel({ caseId, canWrite }: { caseId: string; canWrite: boolean }) {
  const q = useApi<{ inspection: { status: string }; counts: { total: number; checked: number; defect: number } }>(`/api/crm/cases/${caseId}/inspection`);
  const insp = q.data?.inspection;
  return (
    <Card>
      <CardHeader title="Inspección" action={canWrite ? <Link to={`/cases/${caseId}/inspect`} className="btn btn-primary">{insp ? "Abrir inspección" : "Iniciar inspección"}</Link> : undefined} />
      <div className="p-5">
        {q.loading ? <Spinner /> : !insp ? (
          <span className="text-sm text-muted">Checklist profesional guiada para tablet: identificación, carrocería, ruedas, motor, interior, electrónica, prueba dinámica y firma.</span>
        ) : (
          <div className="flex items-center gap-3 text-sm">
            <Badge tone={insp.status === "SIGNED" ? "ok" : "info"}>{insp.status}</Badge>
            <span className="text-muted">{q.data?.counts.checked ?? 0}/{q.data?.counts.total ?? 0} comprobaciones · {q.data?.counts.defect ?? 0} defectos</span>
          </div>
        )}
      </div>
    </Card>
  );
}

function ReportPanel({ caseId, canPublish }: { caseId: string; canPublish: boolean }) {
  const q = useApi<{ status: string; versions: { version: number }[] }>(`/api/crm/cases/${caseId}/report`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const published = q.data?.status === "PUBLISHED";

  async function publish() {
    setBusy(true);
    setError(null);
    try { await api.post(`/api/crm/cases/${caseId}/report/publish`); await q.reload(); }
    catch { setError("El caso debe estar en revisión de informe para publicar."); }
    finally { setBusy(false); }
  }

  return (
    <Card>
      <CardHeader title="Informe" />
      <div className="flex items-center justify-between p-5">
        <div className="text-sm text-muted">
          {q.loading ? "…" : published ? `Publicado · ${q.data?.versions.length ?? 0} versión(es)` : "Sin publicar"}
        </div>
        <div className="flex gap-2">
          {published && <Button variant="ghost" onClick={() => api.openTab(`/api/crm/cases/${caseId}/report/html`)}>Ver informe</Button>}
          {published && <button onClick={() => void api.download(`/api/crm/cases/${caseId}/report/pdf`, `informe-${caseId}.pdf`)} className="text-sm text-muted hover:underline">PDF</button>}
          {canPublish && <Button disabled={busy} onClick={() => void publish()}>{busy ? "Publicando…" : "Publicar informe"}</Button>}
        </div>
      </div>
      {error && <div className="px-5 pb-4"><ErrorText>{error}</ErrorText></div>}
    </Card>
  );
}

function PaymentsPanel({ caseId, query, canWrite, onPaid }: { caseId: string; query: ReturnType<typeof useApi<Page<PaymentRow>>>; canWrite: boolean; onPaid: () => void }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Transferencia SEPA");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const payments = query.data?.data ?? [];

  async function record(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/api/crm/cases/${caseId}/payments`, { amountMinor: Math.round(Number(amount) * 100), method, reference: reference || undefined });
      setAmount(""); setReference("");
      await query.reload();
      onPaid();
    } finally { setBusy(false); }
  }

  return (
    <Card>
      <CardHeader title="Pagos" />
      {payments.length > 0 && (
        <ul className="divide-y divide-line">
          {payments.map((p) => (
            <li key={p.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <span>{p.method ?? "Pago"} {p.reference ? `· ${p.reference}` : ""}</span>
              <span className="flex items-center gap-3"><span className="tnum font-medium">{money(p.amountMinor, p.currency)}</span><Badge tone="ok">{p.status}</Badge></span>
            </li>
          ))}
        </ul>
      )}
      {canWrite && (
        <form onSubmit={(e) => void record(e)} className="flex flex-wrap items-end gap-3 border-t border-line p-5">
          <Field label="Importe (€)"><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required className="max-w-[9rem]" /></Field>
          <Field label="Método">
            <Select value={method} onChange={(e) => setMethod(e.target.value)}>
              <option>Transferencia SEPA</option>
              <option>Tarjeta</option>
              <option>Efectivo</option>
              <option>Otro</option>
            </Select>
          </Field>
          <Field label="Referencia"><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Ref. / justificante" /></Field>
          <Button type="submit" disabled={busy}>Registrar pago</Button>
        </form>
      )}
    </Card>
  );
}

function NotesPanel({ caseId, canWrite }: { caseId: string; canWrite: boolean }) {
  const q = useApi<Page<{ id: string; body: string; authorUserId: string; createdAt: number }>>(`/api/crm/cases/${caseId}/notes`);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const notes = q.data?.data ?? [];

  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/api/crm/cases/${caseId}/notes`, { body }); setBody(""); await q.reload(); } finally { setBusy(false); }
  }

  return (
    <Card>
      <CardHeader title="Notas internas" action={<Badge tone="neutral">Solo personal</Badge>} />
      {notes.length > 0 && (
        <ul className="divide-y divide-line">
          {notes.map((n) => (
            <li key={n.id} className="px-5 py-3">
              <p className="text-sm">{n.body}</p>
              <p className="mt-1 text-xs text-muted">{dateTime(n.createdAt)}</p>
            </li>
          ))}
        </ul>
      )}
      {canWrite && (
        <form onSubmit={(e) => void add(e)} className="flex flex-col gap-2 border-t border-line p-5">
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="Nota interna (nunca visible para el cliente)…" required />
          <div><Button type="submit" disabled={busy || !body}>Añadir nota</Button></div>
        </form>
      )}
    </Card>
  );
}

function TimelinePanel({ history }: { history: HistoryRow[] }) {
  return (
    <Card>
      <CardHeader title="Historial" />
      {history.length === 0 ? (
        <div className="p-6 text-sm text-muted">Sin actividad.</div>
      ) : (
        <ol className="flex flex-col gap-4 px-5 py-4">
          {history.map((h) => (
            <li key={h.id} className="flex gap-3">
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-accent" />
              <div>
                <div className="text-sm">{h.fromStatus ? `${statusLabel(h.fromStatus)} → ` : ""}{statusLabel(h.toStatus)}</div>
                <div className="mt-0.5 text-xs text-muted">{dateTime(h.createdAt)}{h.reason ? ` · ${h.reason}` : ""}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
